/**
 * Compare actual variant images with the other option selections held fixed.
 * An absent assignment is never replaced by the product's gallery image.
 * Shared images do not prove that different option values look different.
 */
export function optionImageChoices(variants, selectedOptions, optionIndex, values) {
  const images = values.map(value => {
    const variant = variants.find(candidate =>
      candidate.options.length === selectedOptions.length &&
      candidate.options.every((option, index) =>
        option === (index === optionIndex ? value : selectedOptions[index])
      )
    );
    return variant?.image || '';
  });
  const assigned = images.filter(Boolean);
  const unique = new Set(assigned);
  const meaningful = unique.size > 1 && unique.size === assigned.length;
  return images.map(image => meaningful ? image : '');
}

function setThumbnail(container, imageUrl) {
  let image = container.querySelector('.cp-option-image');
  if (!imageUrl) {
    image?.remove();
    return;
  }
  if (!image) {
    image = document.createElement('img');
    image.className = 'cp-option-image';
    image.width = 32;
    image.height = 32;
    image.alt = '';
    image.loading = 'lazy';
    container.prepend(image);
  }
  // Replace stale responsive sources as well as src when the context changes.
  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
  if (image.getAttribute('src') !== imageUrl) image.src = imageUrl;
}

function initOptionImages(scope = document) {
  const forms = new Set(Array.from(scope.querySelectorAll('[data-option-image-map]'))
    .map(node => node.closest('form')).filter(Boolean));
  for (const form of forms) {
    if (form.dataset.optionImagesReady) continue;
    const variantNode = form.querySelector('[data-product-variants]');
    if (!variantNode) continue;
    let variants;
    try {
      const images = JSON.parse(form.querySelector('[data-option-image-map]').textContent);
      variants = JSON.parse(variantNode.textContent).map(variant => ({
        options: variant.options,
        image: images[String(variant.id)] || ''
      }));
    } catch {
      continue;
    }
    const selects = Array.from(form.querySelectorAll('[data-product-option]'));
    const groups = Array.from(form.querySelectorAll('[data-option-image-index]'));
    const sync = () => {
      const selectedOptions = selects.map(select => select.value);
      for (const group of groups) {
        const index = Number(group.dataset.optionImageIndex);
        const buttons = Array.from(group.querySelectorAll('[data-pill-value]'));
        const values = buttons.map(button => button.dataset.pillValue);
        const images = optionImageChoices(variants, selectedOptions, index, values);
        buttons.forEach((button, i) => setThumbnail(button, images[i]));
        const summary = group.querySelector('[data-option-selection]');
        if (summary) setThumbnail(summary, images[values.indexOf(selectedOptions[index])] || '');
      }
    };
    form.dataset.optionImagesReady = 'true';
    form.addEventListener('change', event => {
      if (event.target.matches('[data-product-option]')) queueMicrotask(sync);
    });
    sync();
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initOptionImages(), {once: true});
  } else {
    initOptionImages();
  }
  document.addEventListener('shopify:section:load', event => initOptionImages(event.target));
}
